import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";
import GemDivider from "@/components/GemDivider";
import CompetitorCard from "@/components/CompetitorCard";
import { PROMPTBOUND, COMPETITORS, SPARSE_FOOTPRINT_NOTE } from "@/lib/competitorFacts";
import { btnPrimary, btnSecondary, card, label } from "@/lib/styles";

const byId = (id) => COMPETITORS.find((c) => c.id === id);

// Interview Query itself: not in the shared competitor data file because it's
// the subject of this page rather than one of the "alternatives," but it gets
// the same honest treatment (no invented pricing or feature claims).
const INTERVIEW_QUERY = {
  id: "interview-query",
  name: "Interview Query",
  bestFor: "One of the longer-standing names in interview prep",
  priceShort: "Not verified in our research",
  priceDetail:
    "We don't have a verified figure for current pricing tiers. Check interviewquery.com directly for what they're charging today.",
  reviewsShort: "No meaningful review-site footprint found",
  reviewsDetail: SPARSE_FOOTPRINT_NOTE,
  note: "This is genuinely the gap that prompted this page: as of a 2026-07-10 research pass, no independent, non-affiliate comparison of Interview Query against the rest of the field existed. We're not filling that gap with invented details about Interview Query itself either.",
};

export default function InterviewQueryAlternatives() {
  usePageMeta(
    "Interview Query Alternatives (2026): 7 Platforms Compared",
    "Looking for Interview Query alternatives? An honest, sourced comparison of Promptbound, Interview Kickstart, Final Round AI, Educative, Exponent and LeetCode for 2026, with real pricing and review data where it exists."
  );

  const rest = ["educative", "exponent", "final-round-ai", "interview-kickstart", "leetcode"].map(byId);

  return (
    <div className="min-h-screen grid-bg">
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <span className={label}>Comparison guide</span>
        <h1 className="mt-3 font-serif font-bold text-3xl sm:text-4xl text-zinc-100">Interview Query alternatives</h1>
        <p className="mt-5 text-zinc-400 leading-relaxed">
          If you searched "Interview Query alternatives," you've probably noticed there isn't much independent
          comparison content out there for it. Here's an honest one: seven platforms people mention in the same
          breath as Interview Query, including our own product, Promptbound, compared on price and reviews where we
          could actually verify them.
        </p>
        <div className={`${card} mt-6 p-5 flex gap-3 items-start`}>
          <ShieldCheck size={18} className="text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-xs text-zinc-400 leading-relaxed">
            Full disclosure: this page is published by Promptbound. Every claim below, about every product including
            ours, is sourced and dated. Where we don't have a verified number, we say so instead of guessing.
          </p>
        </div>
      </section>

      <GemDivider />

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-serif font-bold text-2xl text-zinc-100 mb-6">The options, compared</h2>
        <div className="space-y-4">
          <CompetitorCard competitor={INTERVIEW_QUERY} />
          {rest.map((c) => <CompetitorCard key={c.id} competitor={c} />)}
          <CompetitorCard competitor={PROMPTBOUND} isPromptbound />
        </div>
        <p className="mt-6 text-xs text-zinc-500 leading-relaxed">
          You'll also see interviewing.io, Hello Interview and NeetCode mentioned in this space. Same caveat as
          Interview Query above: our research found no meaningful review-site footprint for any of them, so we're
          not going to guess at their pricing or content depth here. Check their sites directly.
        </p>
      </section>

      <GemDivider />

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h2 className="font-serif font-bold text-2xl text-zinc-100 mb-4">Which one should you actually use</h2>
        <ul className="space-y-3 text-sm text-zinc-400 leading-relaxed list-disc list-inside">
          <li><span className="text-zinc-200 font-semibold">Free lessons plus graded AI/ML interview practice:</span> Promptbound. All 40 lessons are free forever, and the graded interviews use questions adapted from real Meta, Google, Amazon, OpenAI and Anthropic loops.</li>
          <li><span className="text-zinc-200 font-semibold">A live, instructor-led bootcamp and a five-figure budget:</span> Interview Kickstart, including their ML Flagship track.</li>
          <li><span className="text-zinc-200 font-semibold">AI-driven mock interview reps at a lower monthly price than a bootcamp:</span> Final Round AI, weigh the Trustpilot-versus-Product-Hunt rating gap above first.</li>
          <li><span className="text-zinc-200 font-semibold">Broad technical course content beyond interview prep:</span> Educative.</li>
          <li><span className="text-zinc-200 font-semibold">General tech or PM interview prep:</span> Exponent, though reviewers have flagged it as weaker for AI-specific roles.</li>
          <li><span className="text-zinc-200 font-semibold">Algorithm and data-structure grinding:</span> LeetCode.</li>
          <li><span className="text-zinc-200 font-semibold">Interview Query itself, interviewing.io, Hello Interview or NeetCode:</span> check their sites directly, we don't have enough verified data on any of them to weigh in confidently.</li>
        </ul>

        <div className="mt-14 text-center border border-gold/25 rounded-xl bg-gradient-to-b from-[#141d34]/60 to-transparent p-10 gold-glow">
          <h3 className="font-serif font-bold text-2xl text-zinc-100">Try the free 40-lesson curriculum</h3>
          <p className="text-sm text-zinc-400 mt-2 mb-7 max-w-md mx-auto">
            No card required. Get 3 graded interviews with Altaris on the house, then decide if unlimited is worth
            $20/mo.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/register" data-testid="iqalt-cta-register" className={btnPrimary}>
              Create free account <ArrowRight size={16} />
            </Link>
            <Link to="/pricing" data-testid="iqalt-cta-pricing" className={btnSecondary}>
              See pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
